import { getApiBaseUrl } from '../../../utils/apiConfig';

/**
 * Profile data structure
 */
export interface FormProfile {
  id: string;
  user_id: string;
  profile_name: string;
  form_data: Record<string, any>;
  settings?: Record<string, any>;
  form_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * API response format
 */
interface ApiResponse<T> {
  success: boolean;
  error?: string;
  profile?: T;
  profiles?: T[];
  message?: string;
}

/**
 * Get all profiles for a specific form
 */
export const getProfilesForForm = async (formId: string): Promise<FormProfile[]> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/list?formId=${encodeURIComponent(formId)}`;
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch profiles: ${response.statusText}`);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch profiles');
    }

    return data.profiles || [];
  } catch (error) {
    console.error('Error fetching profiles for form:', error);
    throw error;
  }
};

/**
 * Get all profiles for the current user (across all forms)
 */
export const getAllProfiles = async (): Promise<FormProfile[]> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/list`;
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch profiles: ${response.statusText}`);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch profiles');
    }

    return data.profiles || [];
  } catch (error) {
    console.error('Error fetching all profiles:', error);
    throw error;
  }
};

/**
 * Get a single profile by ID
 */
export const getProfile = async (profileId: string): Promise<FormProfile> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/${profileId}`;
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch profile: ${response.statusText}`);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success || !data.profile) {
      throw new Error(data.error || 'Failed to fetch profile');
    }

    return data.profile;
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

/**
 * Create a new profile
 */
export const createProfile = async (profileData: {
  profile_name: string;
  form_id: string;
  form_data: Record<string, any>;
  settings?: Record<string, any>;
}): Promise<FormProfile> => {
  try {
    console.log('[Profile Service] Creating profile:', {
      profile_name: profileData.profile_name,
      form_id: profileData.form_id,
      has_form_data: !!profileData.form_data
    });

    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/save`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        profile_name: profileData.profile_name,
        form_id: profileData.form_id,
        form_data: profileData.form_data,
        settings: profileData.settings || {}
      })
    });

    if (!response.ok) {
      let errorMessage = `Failed to create profile: ${response.statusText}`;
      try {
        const data: ApiResponse<FormProfile> = await response.json();
        errorMessage = data.error || errorMessage;
      } catch (parseError) {
        console.error('[Profile Service] Failed to parse error response:', parseError);
      }
      console.error('[Profile Service] Create failed:', errorMessage);
      throw new Error(errorMessage);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success || !data.profile) {
      const errorMessage = data.error || 'Failed to create profile';
      console.error('[Profile Service] Create failed:', errorMessage);
      throw new Error(errorMessage);
    }

    console.log('[Profile Service] Profile created successfully:', data.profile.id);
    return data.profile;
  } catch (error) {
    console.error('[Profile Service] Error creating profile:', error);
    throw error;
  }
};

/**
 * Update an existing profile
 */
export const updateProfile = async (
  profileId: string,
  updates: {
    profile_name?: string;
    form_data?: Record<string, any>;
    settings?: Record<string, any>;
  }
): Promise<FormProfile> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/update`;
    const response = await fetch(apiUrl, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: profileId,
        ...updates
      })
    });

    if (!response.ok) {
      const data: ApiResponse<FormProfile> = await response.json();
      throw new Error(data.error || `Failed to update profile: ${response.statusText}`);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success || !data.profile) {
      throw new Error(data.error || 'Failed to update profile');
    }

    return data.profile;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * Delete a profile
 */
export const deleteProfile = async (profileId: string): Promise<void> => {
  try {
    const apiUrl = `${getApiBaseUrl()}/api/ai-sequence/profile/${profileId}`;
    const response = await fetch(apiUrl, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const data: ApiResponse<FormProfile> = await response.json();
      throw new Error(data.error || `Failed to delete profile: ${response.statusText}`);
    }

    const data: ApiResponse<FormProfile> = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete profile');
    }
  } catch (error) {
    console.error('Error deleting profile:', error);
    throw error;
  }
};
